import { PropAsset } from './PropAsset';

// Stairs - Merdiven
// Isometric staircase going up
export class Stairs extends PropAsset {
  readonly id = 'stairs';
  readonly width = 64;
  readonly height = 70;
  readonly walkable = true; // Player can walk on stairs
  readonly collisionRadius = 0;
  readonly occludesPlayer = true;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    // Colors based on variant
    const stairColors = [
      { main: '#8a8a8a', dark: '#5a5a5a', light: '#aaaaaa' }, // Stone
      { main: '#7a6a5a', dark: '#5a4a3a', light: '#9a8a7a' }, // Wood
      { main: '#6a5a5a', dark: '#4a3a3a', light: '#8a7a7a' }, // Dark stone
    ];
    const colors = stairColors[v % stairColors.length];

    const stepCount = 5;
    const stepWidth = 40 * s;
    const stepHeight = 8 * s;
    const stepDepth = 12 * s;
    const totalRise = stepCount * stepHeight;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 25 * s, 12 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Draw steps from back to front
    for (let i = stepCount - 1; i >= 0; i--) {
      const stepY = cy - (stepCount - 1 - i) * stepHeight;
      const stepRise = (stepCount - 1 - i) * stepHeight;

      // Step top (flat surface to walk on)
      ctx.fillStyle = colors.light;
      ctx.beginPath();
      ctx.moveTo(cx - stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.lineTo(cx, stepY - stepRise - stepHeight - stepDepth / 2);
      ctx.lineTo(cx + stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.lineTo(cx, stepY - stepRise - stepHeight + stepDepth / 2);
      ctx.closePath();
      ctx.fill();

      // Step front face
      ctx.fillStyle = colors.main;
      ctx.beginPath();
      ctx.moveTo(cx - stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.lineTo(cx - stepWidth / 2, stepY - stepRise);
      ctx.lineTo(cx, stepY - stepRise + stepDepth / 2);
      ctx.lineTo(cx, stepY - stepRise - stepHeight + stepDepth / 2);
      ctx.closePath();
      ctx.fill();

      // Step right face
      ctx.fillStyle = colors.dark;
      ctx.beginPath();
      ctx.moveTo(cx, stepY - stepRise - stepHeight + stepDepth / 2);
      ctx.lineTo(cx, stepY - stepRise + stepDepth / 2);
      ctx.lineTo(cx + stepWidth / 2, stepY - stepRise);
      ctx.lineTo(cx + stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.closePath();
      ctx.fill();

      // Edge highlight
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1 * s;
      ctx.beginPath();
      ctx.moveTo(cx - stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.lineTo(cx, stepY - stepRise - stepHeight - stepDepth / 2);
      ctx.lineTo(cx + stepWidth / 2, stepY - stepRise - stepHeight);
      ctx.stroke();
    }

    // Side rails (optional for some variants)
    if (v === 1) {
      ctx.fillStyle = '#5a4a3a';
      // Left rail
      ctx.fillRect(cx - stepWidth / 2 - 3 * s, cy - totalRise - 20 * s, 3 * s, totalRise + 15 * s);
      // Right rail
      ctx.fillRect(cx + stepWidth / 2, cy - totalRise - 20 * s, 3 * s, totalRise + 15 * s);
    }
  }
}

// Stairs Down - Aşağı Merdiven
export class StairsDown extends PropAsset {
  readonly id = 'stairs_down';
  readonly width = 64;
  readonly height = 50;
  readonly walkable = true;
  readonly collisionRadius = 0;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const stairColors = [
      { main: '#6a6a6a', dark: '#4a4a4a', light: '#8a8a8a' },
      { main: '#5a4a3a', dark: '#3a2a1a', light: '#7a6a5a' },
    ];
    const colors = stairColors[v % stairColors.length];

    const stepCount = 4;
    const stepWidth = 36 * s;
    const stepHeight = 6 * s;
    const stepDepth = 10 * s;

    // Dark hole effect
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 5 * s, 20 * s, 15 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Draw steps going down (into the ground)
    for (let i = 0; i < stepCount; i++) {
      const stepY = cy + i * stepHeight * 0.5;

      // Step top
      ctx.fillStyle = colors.light;
      ctx.globalAlpha = 1 - i * 0.2;
      ctx.beginPath();
      ctx.moveTo(cx - stepWidth / 2 + i * 3 * s, stepY);
      ctx.lineTo(cx, stepY - stepDepth / 2);
      ctx.lineTo(cx + stepWidth / 2 - i * 3 * s, stepY);
      ctx.lineTo(cx, stepY + stepDepth / 2);
      ctx.closePath();
      ctx.fill();

      // Step front
      ctx.fillStyle = colors.main;
      ctx.beginPath();
      ctx.moveTo(cx - stepWidth / 2 + i * 3 * s, stepY);
      ctx.lineTo(cx, stepY + stepDepth / 2);
      ctx.lineTo(cx, stepY + stepDepth / 2 + stepHeight);
      ctx.lineTo(cx - stepWidth / 2 + i * 3 * s, stepY + stepHeight);
      ctx.closePath();
      ctx.fill();
    }

    ctx.globalAlpha = 1;

    // Frame around the opening
    ctx.strokeStyle = colors.dark;
    ctx.lineWidth = 2 * s;
    ctx.beginPath();
    ctx.moveTo(cx - stepWidth / 2, cy);
    ctx.lineTo(cx, cy - stepDepth / 2);
    ctx.lineTo(cx + stepWidth / 2, cy);
    ctx.stroke();
  }
}
